import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import _ from 'lodash';
import { useStyles } from '../../styles/styles';
import ImageTextRow from '../../components/cards/CardRows/ImageTextRow';
import LocationImagesGrid from '../../components/cards/ImageGridCard';
import StyledCard from '../../components/cards/StyledCard';
import StyledCardHeader, { HeaderButtonIcon } from '../../components/cards/StyledCardHeader';
import StyledCardContent from '../../components/cards/StyledCardContent';
import { drawerStore } from '../../state/drawerStore';
import GoogleMapsWrapper from '../../components/google/GoogleMapsWrapper';
import { FormInputTypes } from '../../utils/constants';
import { NameValueRowList } from '../../components/cards/CardRows/NameValueRowList';
import Loading from '../../components/other/Loading';
import { Api } from '../../api';
import { useProvider } from '../../state/providerStore';
import { Button } from '@material-ui/core';

const detailsRows = [
  { name: 'isRecommended', label: 'Recommended' },
  'description',
  'workingHours',
  { name: 'isPayableParking', label: 'Free parking', invertValue: true },
];
const contactRows = [
  { name: 'contactPhone', label: 'Phone' },
  { name: 'contactEmail', label: 'Email' },
];
const locationRows = ['latitude', 'longitude'];

const editDetailsInputs = [
  { name: 'isRecommended', label: 'Recommended', type: FormInputTypes.switch },
  { name: 'description', type: FormInputTypes.textField },
  { name: 'workingHours', type: FormInputTypes.textField },
  {
    name: 'isPayableParking',
    label: 'Free parking',
    type: FormInputTypes.switch,
    invertValue: true,
  },
];

const editContactInputs = [
  { name: 'contactPhone', type: FormInputTypes.textField },
  { name: 'contactEmail', type: FormInputTypes.textField },
];

const editLocationInputs = [
  { name: 'latitude', type: FormInputTypes.textField },
  { name: 'longitude', type: FormInputTypes.textField },
];

// /locations/activities Api returns ActivityObj[] array with all possible activities
interface ActivityObj {
  group: string;
  name: string;
  activities: string[];
}

const getActivityHash = (group: string, activityName: string) => `${group}_${activityName}`;

const getActivityInputs = (allActivities: ActivityObj[]) => {
  const inputs = [];

  allActivities?.forEach((obj: ActivityObj) => {
    inputs.push({ name: obj.group, type: FormInputTypes.miniTitle });

    obj.activities?.forEach((activityName) => {
      const activityHash = getActivityHash(obj.group, activityName);

      inputs.push({
        name: activityHash,
        label: activityName,
        type: FormInputTypes.checkbox,
        defaultValue: true,
      });
    });
  });

  return inputs;
};

const LocationDetailsDetails = observer(() => {
  const { locationid } = useParams<{ locationid: string }>();

  const locationProvider = useProvider(() =>
    Api.charge.getLocation(locationid).catch((err) => {
      console.error('Error fetching location:', err);
      return null;
    }),
  );
  const locationActivitiesProvider = useProvider(() =>
    Api.charge.listActivitiesForLocation(locationid).catch((err) => {
      console.error('Error fetching location activities:', err);
      return [];
    }),
  );

  const [loading, setLoading] = useState(false);

  const classes = useStyles();

  // TODO find a way to toggle activitiesByGroup on drawer switch onChange callback

  const confirmEditDetails = (values) => {
    Api.charge
      .patchLocation(locationid, {
        isRecommended: values.isRecommended,
        description: values.description,
        workingHours: values.workingHours,
        isPayableParking: values.isPayableParking,
      })
      .then(locationProvider.reloadResource)
      .catch((error) => {
        console.error('Error updating location details:', error);
      });
  };

  const confirmEditContact = (values) => {
    Api.charge
      .patchLocation(locationid, {
        contactPhone: values.contactPhone,
        contactEmail: values.contactEmail,
      })
      .then(locationProvider.reloadResource)
      .catch((error) => {
        console.error('Error updating location contact:', error);
      });
  };

  const confirmEditLocation = (values) => {
    values = _.pick(values, locationRows);
    Api.charge
      .patchLocation(locationid, values)
      .then(locationProvider.reloadResource)
      .catch((error) => {
        console.error('Error updating location coordinates:', error);
      });
  };

  const confirmAddActivity = (values) => {
    Api.charge
      .deleteActivitiesForLocation(locationid, { names: values })
      .then(locationProvider.reloadResource)
      .then(locationActivitiesProvider.reloadResource)
      .catch((error) => {
        console.error('Error updating location activities:', error);
      });
  };

  const onClickEditDetails = () => {
    drawerStore.openDrawer({
      label: 'Edit details',
      inputs: editDetailsInputs,
      clickCallback: () => confirmEditDetails(toJS(drawerStore.values)),
    });
    drawerStore.setValues(locationProvider.data);
  };

  const onClickEditContact = () => {
    drawerStore.openDrawer({
      label: 'Edit contact',
      inputs: editContactInputs,
      clickCallback: () => confirmEditContact(toJS(drawerStore.values)),
    });

    drawerStore.setValues(locationProvider.data);
  };

  const onClickEditLocation = () => {
    drawerStore.openDrawer({
      label: 'Edit location',
      inputs: editLocationInputs,
      clickCallback: () => confirmEditLocation(toJS(drawerStore.values)),
    });

    drawerStore.setValues(locationProvider.data);
  };

  const onClickAddActivity = () => {
    drawerStore.openDrawer({
      label: 'Update activities',
      inputs: getActivityInputs(locationActivitiesProvider.data),
      clickCallback: () => confirmAddActivity(toJS(drawerStore.values)),
    });
  };

  const syncLocationActivitiesClicked = () => {
    setLoading(true);

    Api.charge
      .syncLocationActivities(locationid)
      .then(() => {
        locationActivitiesProvider.reloadResource();
      })
      .catch((error) => {
        console.error('Error syncing location activities:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const googleMapsDragCallback = (lat: number, lng: number) => {
    const values = { latitude: lat.toString(), longitude: lng.toString() };
    Api.charge
      .patchLocation(locationid, values)
      .then(locationProvider.reloadResource)
      .catch((error) => {
        console.error('Error updating location on drag:', error);
      });
  };

  const pos = {
    lat: parseFloat(locationProvider.data?.latitude),
    lng: parseFloat(locationProvider.data?.longitude),
  };

  return (
    <div className={classes.cardRows}>
      <div className={classes.leftCardRow}>
        <StyledCard>
          <StyledCardHeader
            title="Details"
            onClick={onClickEditDetails}
            headerButtonIcon={HeaderButtonIcon.edit}
          />
          <StyledCardContent>
            <NameValueRowList rows={detailsRows} values={locationProvider.data} />
          </StyledCardContent>
        </StyledCard>

        <StyledCard>
          <StyledCardHeader
            title="Contact"
            onClick={onClickEditContact}
            headerButtonIcon={HeaderButtonIcon.edit}
          />
          <StyledCardContent>
            <NameValueRowList rows={contactRows} values={locationProvider.data} />
          </StyledCardContent>
        </StyledCard>

        <LocationImagesGrid entityId={locationid} type="location" />
      </div>

      <div className={classes.rightCardRow}>
        <StyledCard>
          <StyledCardHeader
            title="Precise location"
            onClick={onClickEditLocation}
            headerButtonIcon={HeaderButtonIcon.edit}
          />
          <StyledCardContent>
            <NameValueRowList rows={locationRows} values={locationProvider.data} />
          </StyledCardContent>
          <div
            style={{
              display: 'flex',
              alignContent: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 32,
              marginBottom: 32,
              paddingLeft: 32,
              paddingRight: 32,
              height: '20vw',
            }}
          >
            <GoogleMapsWrapper
              pos={pos}
              zoom={15}
              dragCallback={googleMapsDragCallback}
              showLocationMarker
            />
          </div>
        </StyledCard>

        <StyledCard>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              marginTop: '15px',
            }}
          >
            <Button
              variant="text"
              color="primary"
              onClick={syncLocationActivitiesClicked}
              disabled={loading}
            >
              {loading ? 'Syncing...' : 'Sync Activities'}
            </Button>
            {loading && <Loading />}
          </div>
          <StyledCardHeader
            title="Activities"
            onClick={onClickAddActivity}
            headerButtonIcon={HeaderButtonIcon.add}
          />
          <StyledCardContent>
            {locationActivitiesProvider.data?.map((group, idx) => (
              <ImageTextRow key={`${group.group}_${idx}`} label={group.group} />
            ))}
          </StyledCardContent>
        </StyledCard>
      </div>
    </div>
  );
});

export default LocationDetailsDetails;
